import React, { FC, useRef, useState, useEffect } from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  ScrollView,
  Pressable,
  Button,
} from "native-base";
import * as AV from "../../AVCore/AVCore";
import Tooltip from "@mui/material/Tooltip";
import { ExpandIcon, CollapseIcon } from "../../assets/icons/icon-assets";
import AVCancel from "./AVCancel";
import { useWindowDimension } from "../../Utility/WindowDimensions";
import { Loc } from "../../App/Loc";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import {
  setPostPopupExpanded,
  setLiveStreamToken,
} from "../../Redux/Actions/userActions";
import AgoraRTC from "agora-rtc-sdk-ng";

/* -------------------- AGORA CONFIG -------------------- */
const AGORA_APP_ID = "914bc52b86f54cec8d68aa41a00e2ec6";
const CHANNEL_NAME = "test_live_channel";
const TEMP_TOKEN =
  "007eJxTYOjWdHkZ8lKbc+K+D/Luh30MjZpkJQyO7zCZUbt64U91tyAFBktDk6RkU6MkC7M0U5Pk1GSLFDOLxEQTw0QDg1Sj1GSzXyzFmQ2BjAy/Q5SZGRkgEMQXZChJLS6Jz8ksS41PzkjMy0vNYWAAAJS8I4c=";

interface AVLiveHostPopupProps {
  onClose: () => void;
}

const AVLiveHostPopup: FC<AVLiveHostPopupProps> = ({ onClose }) => {
  const dimensions = useWindowDimension();
  const mobile = dimensions?.width < 600;
  const dispatch = useDispatch();

  const [isExpanded, setIsExpanded] = useState(false);

  const clientRef = useRef<any>(null);
  const tracksRef = useRef<any[]>([]);
  const localVideoRef = useRef<HTMLDivElement | null>(null);

  const currentUser = useSelector(
    (state: RootStateOrAny) => state?.user?.user[0],
  );
  const liveStreamToken = useSelector(
    (state: RootStateOrAny) => state.user.liveStreamToken,
  );

  /* -------------------- START LIVE -------------------- */
  useEffect(() => {
    startLive();

    return () => {
      (async () => {
        await leaveLive();
      })();
    };
  }, []);

  const startLive = async () => {
    const client = AgoraRTC.createClient({ mode: "live", codec: "vp8" });
    clientRef.current = client;

    await client.setClientRole("host");

    try {
      let token = liveStreamToken;

      // If no token, fetch first
      if (!token) {
        token = await AV.AVEntity.getSecret(currentUser?.region);
          dispatch(setLiveStreamToken(token));
      }

      await client.join(AGORA_APP_ID, CHANNEL_NAME, token, null);

      console.log("Host joined channel");
    } catch (error) {
      console.log("Token expired or invalid, fetching new one...", error);

      const newToken = await AV.AVEntity.getSecret(currentUser?.region);

      if (newToken) {
        dispatch(setLiveStreamToken(newToken));
        await client.join(AGORA_APP_ID, CHANNEL_NAME, newToken, null);
        console.log("Host joined with new token");
      }
    }

    const tracks = await AgoraRTC.createMicrophoneAndCameraTracks();
    tracksRef.current = tracks;

    tracks[1].play(localVideoRef.current!); // camera track
    await client.publish(tracks);

    console.log("HOST LIVE");
  };

  const leaveLive = async () => {
    try {
      tracksRef.current.forEach((track) => {
        track.stop();
        track.close();
      });
      tracksRef.current = [];
      await clientRef.current?.leave();
    } catch (e) {
      console.warn("Leave live error", e);
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <Box
      position="fixed"
      bg="rgba(0,0,0,0.25)"
      top={0}
      bottom={0}
      left={0}
      right={0}
      zIndex={99999}
    >
      <div
        className={`create_post_popup ${mobile && "create_post_popup_mobile"}`}
        style={{
          width: isExpanded
            ? mobile
              ? "100%"
              : "90%"
            : mobile
              ? dimensions.width - 15
              : "700px",
          height: isExpanded ? (mobile ? "100%" : "90vh") : "60vh",
          maxHeight: isExpanded ? (mobile ? "100%" : "90vh") : "60vh",
          padding: isExpanded ? "30px" : "24px",
          top: isExpanded ? "50%" : "55%",
        }}
      >
        <VStack height="100%">
          {/* HEADER */}
          <HStack
            justifyContent="space-between"
            alignItems="center"
            borderBottomWidth={1}
            borderColor="AVColor.grey"
            pb={3}
          >
            <Text fontSize={24}>Host Live</Text>

            <HStack space={3} alignItems="center">
              <Tooltip
                title={
                  isExpanded
                    ? Loc.currentLang.postPopup.collapse
                    : Loc.currentLang.postPopup.expand
                }
                arrow
              >
                <Pressable
                  onPress={() => {
                    setIsExpanded((p) => {
                      dispatch(setPostPopupExpanded(!p));
                      return !p;
                    });
                  }}
                >
                  {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                </Pressable>
              </Tooltip>

              <AVCancel
                callback={() => {
                  leaveLive();
                  onClose();
                  dispatch(setPostPopupExpanded(false));
                }}
                color={undefined}
                size={undefined}
              />
            </HStack>
          </HStack>

          {/* BODY */}
          <ScrollView
            showsVerticalScrollIndicator={false}
            maxHeight={isExpanded ? "78vh" : "45vh"}
          >
            <Box
              mt={6}
              height={isExpanded ? "61vh" : "40vh"}
              borderWidth={1}
              borderRadius={5}
              borderColor="AVColor.grey"
              bg="black"
              position="relative"
            >
              {/* LOCAL VIDEO */}
              <Box
                ref={localVideoRef}
                width="100%"
                height="100%"
                borderRadius={5}
                overflow="hidden"
              />
            </Box>
          </ScrollView>

          {/* FOOTER */}
          <HStack justifyContent="flex-end" space={3} mt={4}>
            <Button
              height="40px"
              width="180px"
              bg="rgba(238,238,238,1)"
              borderRadius="full"
              onPress={() => {
                leaveLive();
                onClose();
                dispatch(setPostPopupExpanded(false));
              }}
            >
              <Text color="rgba(0,0,0,0.5)">
                {Loc.currentLang.postPopup.cancel}
              </Text>
            </Button>

            {/*<Button*/}
            {/*  height="40px"*/}
            {/*  width="180px"*/}
            {/*  bg="AVColor.primary"*/}
            {/*  borderRadius="full"*/}
            {/*>*/}
            {/*  <Text color="white">*/}
            {/*    {Loc.currentLang.postPopup.publish}*/}
            {/*  </Text>*/}
            {/*</Button>*/}
          </HStack>
        </VStack>
      </div>
    </Box>
  );
};

export default AVLiveHostPopup;
